/**
 * Utilitários de download reutilizáveis em qualquer ponto da aplicação.
 *
 * Centraliza a criação do link temporário (`<a download>`), que antes estava
 * duplicada e vazava object URLs. Use:
 *  - `downloadBlob`  — quando já se tem um Blob/ArrayBuffer (ex.: resposta de API).
 *  - `downloadAsset` — para arquivos estáticos servidos de `src/assets`.
 *  - `downloadFromUrl` — para qualquer URL já pronta (inclusive absoluta).
 */

/** Dispara o download de uma URL já existente, sem revogá-la. */
function triggerDownload(url: string, fileName?: string, openInNewTab = false): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  if (fileName) anchor.download = fileName;
  if (openInNewTab) anchor.target = '_blank';
  // rel=noopener evita que a aba aberta tenha acesso a window.opener.
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Baixa um conteúdo binário já carregado em memória.
 * O object URL é revogado após o clique — a versão antiga desta função não
 * revogava, mantendo o buffer preso em memória enquanto a aba vivesse.
 */
export function downloadBlob(data: BlobPart, fileName: string, mimeType?: string): void {
  const blob = data instanceof Blob ? data : new Blob([data], mimeType ? { type: mimeType } : undefined);
  const url = window.URL.createObjectURL(blob);
  try {
    triggerDownload(url, fileName);
  } finally {
    // O clique é síncrono, mas o navegador só lê a URL no próximo tick.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
}

/** Baixa uma URL qualquer (mesma origem ou absoluta). */
export function downloadFromUrl(url: string, fileName?: string): void {
  triggerDownload(url, fileName);
}

/**
 * Baixa um arquivo estático de `src/assets`, que o Angular publica como `/assets/...`.
 *
 * O caminho é codificado por segmento — os templates da Horizon têm espaços e
 * parênteses no nome, que quebrariam a URL se passados crus. Aceita caminhos
 * com ou sem o prefixo `assets/`.
 *
 * @param assetPath ex.: 'files/TERMO DE ADESÃO (1).docx'
 * @param fileName  nome sugerido ao salvar; por padrão, o nome do próprio arquivo.
 */
export function downloadAsset(assetPath: string, fileName?: string): void {
  const normalized = assetPath.replace(/^\/+/, '').replace(/^assets\//, '');
  const encoded = normalized.split('/').map(encodeURIComponent).join('/');
  const suggestedName = fileName ?? normalized.split('/').pop() ?? 'download';
  triggerDownload(`assets/${encoded}`, suggestedName);
}
